'use client';

import Link from '~/(tenant)/_components/tenant-link';

import { Instagram, MapPin, MessageCircle, Youtube } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WHATSAPP_URL = 'https://wa.me/966508686446';
const INSTAGRAM_URL = 'https://www.instagram.com/karama_yoga/';
const YOUTUBE_URL = 'https://www.youtube.com/channel/UCP9wknPaekzfeVLzazVKh_A';

// Bilingual copy is kept inline (EN + AR) to match the landing page body
// (index.tsx) — tenant-specific marketing copy stays self-contained rather
// than polluting the shared `landing.json` namespace. RTL direction flips
// automatically from the <html dir> set in app/layout.tsx for the `ar` locale.
type Bilingual = { en: string; ar: string };

const studioLinks: { label: Bilingual; href: string }[] = [
  { label: { en: 'Book a Class', ar: 'احجزوا كلاس' }, href: '/book' },
  { label: { en: 'Our Classes', ar: 'كلاساتنا' }, href: '/#classes' },
  { label: { en: 'About Karama', ar: 'عن كراما' }, href: '/#about' },
  { label: { en: 'Meet Lana', ar: 'تعرّفوا على لنا' }, href: '/#founder' },
  { label: { en: 'Reviews', ar: 'التقييمات' }, href: '/#reviews' },
];

const legalLinks: { label: Bilingual; href: string }[] = [
  { label: { en: 'Privacy Policy', ar: 'سياسة الخصوصية' }, href: '/privacy-policy' },
  { label: { en: 'Terms of Service', ar: 'شروط الخدمة' }, href: '/terms-of-service' },
];

// logoUrl is part of the shared LandingPageFooterProps contract (the registry
// passes it to every footer). The Karama footer no longer renders a logo, so
// the prop is accepted but unused.
interface KaramaFooterProps {
  logoUrl?: string | null;
}

export function KaramaFooter(_props: KaramaFooterProps) {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith('ar') ?? false;
  const pick = (s: Bilingual) => (isAr ? s.ar : s.en);

  return (
    <footer id="contact" className="border-t border-gray-100 bg-white py-14">
      {/* Matches the header bar's max width (1456px) but keeps comfortable
          left/right padding so the footer text doesn't hug the screen edge. */}
      <div className="mx-auto max-w-[1456px] px-6 lg:px-8">
        <div className="mb-10 grid gap-10 md:grid-cols-4">
          {/* Brand */}
          <div className="md:col-span-2">
            <p className="max-w-xs text-sm leading-relaxed text-gray-500">
              {isAr
                ? 'ليس مجرد استوديو. بل تجربة. شعور. تحوّل. بدأت في المملكة العربية السعودية، وتتوسّع عالمياً.'
                : 'Not just a studio. An experience. A feeling. A shift. Starting in Saudi Arabia, expanding globally.'}
            </p>
            <div className="mt-5 flex gap-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-9 w-9 items-center justify-center border border-gray-200 text-gray-600 transition-colors hover:border-gray-400"
              >
                <Instagram size={15} />
              </a>
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="flex h-9 w-9 items-center justify-center border border-gray-200 text-gray-600 transition-colors hover:border-gray-400"
              >
                <Youtube size={15} />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="flex h-9 w-9 items-center justify-center border border-gray-200 text-gray-600 transition-colors hover:border-gray-400"
              >
                <MessageCircle size={15} />
              </a>
            </div>
          </div>

          {/* Studio links */}
          <div>
            <h4
              className="font-karama-label mb-4 text-xs"
              style={{ color: 'var(--karama-purple)' }}
            >
              {isAr ? 'الاستوديو' : 'Studio'}
            </h4>
            <div className="flex flex-col gap-2.5">
              {studioLinks.map((link) => (
                <Link
                  key={link.label.en}
                  href={link.href}
                  className="text-sm text-gray-500 transition-colors hover:text-gray-800"
                >
                  {pick(link.label)}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4
              className="font-karama-label mb-4 text-xs"
              style={{ color: 'var(--karama-teal)' }}
            >
              {isAr ? 'تواصلوا معنا' : 'Contact'}
            </h4>
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2.5">
                <MapPin
                  size={14}
                  className="mt-0.5 flex-shrink-0 text-gray-400"
                />
                <p className="text-sm text-gray-500">
                  {isAr
                    ? '٧٧٥٠ شارع حراء، حي الشاطئ، جدة ٢٣٥١٤، المملكة العربية السعودية'
                    : '7750 Hira St, Ash Shati, Jeddah 23514, Saudi Arabia'}
                </p>
              </div>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-gray-500 transition-colors hover:text-gray-800"
              >
                <MessageCircle
                  size={14}
                  className="flex-shrink-0 text-gray-400"
                />
                {isAr ? 'راسلونا عبر واتساب' : 'WhatsApp Us'}
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-gray-500 transition-colors hover:text-gray-800"
              >
                <Instagram size={14} className="flex-shrink-0 text-gray-400" />
                @karama_yoga
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 pt-8 sm:flex-row">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <p className="text-xs text-gray-400">
              ©{' '}
              {isAr
                ? `${new Date().getFullYear()} كراما يوغا ستوديو. جميع الحقوق محفوظة.`
                : `${new Date().getFullYear()} Karama Yoga Studio. All rights reserved.`}
            </p>
            {legalLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-xs text-gray-400 transition-colors hover:text-gray-700"
              >
                {pick(link.label)}
              </Link>
            ))}
          </div>
          <p className="text-xs text-gray-400">
            {isAr ? 'صُنع في السعودية 🇸🇦' : 'Made in Saudi 🇸🇦'}
            <span className="mx-2">•</span>
            {isAr ? 'مشغّل بواسطة ' : 'Powered by '}
            <a
              href="https://askflow.sa"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:opacity-80"
              style={{ color: 'var(--karama-teal)' }}
            >
              askflow.sa
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
